import { h } from "./../../utils/createElement";
import { __extends } from "tslib";
import * as React from 'react';
import { ImageStatus } from './interface';
import { consumeTheme } from '../../utils/theme';
import { castInto, emptyFn } from '../../utils/helper';
import ua from '@tencent/docs-user-agent';
import Loading from '../Loading';
import { i18n } from '../../common/locale';
import DelayWrapper from '../../utils/DelayWrapper';
import { calcClippedResult } from './util';
import { DEFAULT_MIN_SCALE, FIREFOX_ZOOM_SENSITIVITY, PINCH_ZOOM_SENSITIVITY, WHEEL_ZOOM_SENSITIVITY } from './constant';
var LoadableImage = (function (_super) {
    __extends(LoadableImage, _super);
    function LoadableImage() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.imageWrapperRef = React.createRef();
        _this.imageRef = React.createRef();
        _this.isMouseDragging = false;
        _this.minScale = DEFAULT_MIN_SCALE;
        _this.adaptScale = 1;
        _this.state = {
            scale: 1,
            translateX: 0,
            translateY: 0,
            status: ImageStatus.LOADING,
        };
        _this.handleLoad = function () {
            setTimeout(function () {
                var _a, _b;
                var adaptScale = _this.calcCurrentImageAdaptScale();
                _this.minScale = Math.min(adaptScale, DEFAULT_MIN_SCALE);
                _this.adaptScale = adaptScale;
                _this.scaleTo(adaptScale);
                _this.setState({
                    translateX: 0,
                    translateY: 0,
                    status: ImageStatus.SUCCESS,
                });
                (_b = (_a = _this.props).onLoad) === null || _b === void 0 ? void 0 : _b.call(_a);
            }, 0);
        };
        _this.handleError = function (e) {
            _this.setState({
                status: ImageStatus.FAILED,
            });
            _this.props.onError(e);
        };
        _this.handleMouseWheel = function (e) {
            e.preventDefault();
            var ctrlKey = e.ctrlKey, deltaY = e.deltaY;
            var effectiveDeltaY = (ua.isFirefox && ua.isWindows) ? _this.processDeltaYForFirefox(deltaY) : deltaY;
            var sensitivity = ctrlKey ? PINCH_ZOOM_SENSITIVITY : WHEEL_ZOOM_SENSITIVITY;
            var delta = effectiveDeltaY * sensitivity;
            _this.scaleByWheel(delta);
        };
        _this.handleMouseDown = function (e) {
            var _a, _b;
            _this.isMouseDragging = true;
            e.stopPropagation();
            document.addEventListener('mousemove', _this.handleMouseMove);
            document.addEventListener('mouseup', _this.handleMouseEnd);
            (_b = (_a = _this.props).onMouseDown) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        };
        _this.handleMouseMove = function (e) {
            if (!_this.isMouseDragging || !_this.imageWrapperRef.current) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            var _a = _this.state, translateX = _a.translateX, translateY = _a.translateY, scale = _a.scale;
            var _b = _this.getDistances(), leftDistance = _b.leftDistance, rightDistance = _b.rightDistance, topDistance = _b.topDistance, bottomDistance = _b.bottomDistance;
            var movementX = e.movementX, movementY = e.movementY;
            if ((movementX <= 0 && rightDistance > 0) || (movementX > 0 && leftDistance > 0)) {
                movementX = 0;
            }
            if ((movementY <= 0 && bottomDistance > 0) || (movementY > 0 && topDistance > 0)) {
                movementY = 0;
            }
            translateX = translateX + movementX / scale;
            translateY = translateY + movementY / scale;
            _this.moveTo(translateX, translateY);
        };
        _this.handleMouseEnd = function () {
            _this.isMouseDragging = false;
            document.removeEventListener('mousemove', _this.handleMouseMove);
            document.removeEventListener('mouseup', _this.handleMouseEnd);
        };
        _this.themedRender = function (classNames) {
            var _a, _b;
            var _c = _this.props, prefixCls = _c.prefixCls, className = _c.className, style = _c.style, url = _c.url, previewUrl = _c.previewUrl, clipStyle = _c.clipStyle, onContextMenu = _c.onContextMenu, preloadUrls = _c.preloadUrls;
            var status = _this.state.status;
            var imageLoadingClass = classNames(prefixCls + "-loading");
            var imageErrorClass = classNames(prefixCls + "-error");
            var imageClass = classNames(prefixCls, (_a = {},
                _a[prefixCls + "-success"] = status === ImageStatus.SUCCESS,
                _a));
            var previewImageClass = classNames(prefixCls + "-preview");
            var imageContainerClass = classNames(prefixCls + "-container", className);
            var imageWrapperContainerClass = classNames(prefixCls + "-wrapper-container", (_b = {},
                _b[prefixCls + "-wrapper-container-draggable"] = _this.isDraggable(),
                _b[prefixCls + "-wrapper-container-clipped"] = clipStyle,
                _b));
            var preloadImageClass = classNames(prefixCls + "-preload");
            return (h("div", { className: imageContainerClass, style: style },
                h("div", { className: imageWrapperContainerClass, style: _this.getImageWrapperStyle(), ref: _this.imageWrapperRef },
                    h("img", { className: imageClass, style: _this.getImageStyle(), src: url, key: url, ref: _this.imageRef, onLoad: _this.handleLoad, onError: _this.handleError, onDragStart: function (e) { return e.preventDefault(); }, onMouseDown: _this.handleMouseDown, onContextMenu: onContextMenu }),
                    previewUrl && status === ImageStatus.LOADING && h("img", { className: previewImageClass, style: _this.getImageStyle(), src: previewUrl, key: previewUrl, onDragStart: function (e) { return e.preventDefault(); } })),
                status === ImageStatus.LOADING
                    && h(DelayWrapper, null,
                        h(Loading, { className: imageLoadingClass, spinType: "light" })),
                status === ImageStatus.FAILED && h("div", { className: imageErrorClass }, i18n('loadingFailed')), preloadUrls === null || preloadUrls === void 0 ? void 0 :
                preloadUrls.map(function (preloadUrl) {
                    return preloadUrl && h("img", { key: preloadUrl, className: preloadImageClass, src: preloadUrl });
                })));
        };
        return _this;
    }
    LoadableImage.getDerivedStateFromProps = function (props, state) {
        if (typeof props.scale === 'number') {
            return { scale: props.scale };
        }
        return null;
    };
    Object.defineProperty(LoadableImage.prototype, "rootContainer", {
        get: function () {
            return this.props.rootDomRef.current;
        },
        enumerable: false,
        configurable: true
    });
    LoadableImage.prototype.componentDidMount = function () {
        var _this = this;
        setTimeout(function () {
            _this.rootContainer.addEventListener('wheel', _this.handleMouseWheel, { passive: false });
        }, 0);
    };
    LoadableImage.prototype.componentDidUpdate = function (prevProps, prevState) {
        if (prevState.scale !== this.state.scale) {
            this.adaptImagePosition();
        }
        if (prevProps.url !== this.props.url) {
            this.setState({
                status: ImageStatus.LOADING
            });
        }
    };
    LoadableImage.prototype.isDraggable = function () {
        var image = this.imageWrapperRef.current;
        var scale = this.state.scale;
        if (!image || !this.rootContainer) {
            return false;
        }
        var _a = this.rootContainer.getBoundingClientRect(), width = _a.width, height = _a.height;
        return image.clientWidth * scale > width
            || image.clientHeight * scale > height;
    };
    LoadableImage.prototype.scaleTo = function (scale) {
        var _a, _b;
        var _c = this, minScale = _c.minScale, imageWrapperRef = _c.imageWrapperRef;
        var imageWrapper = imageWrapperRef.current;
        var maxScale = this.props.maxScale;
        if (!imageWrapper) {
            return;
        }
        this.setState({
            scale: castInto(scale, [minScale, maxScale]),
        });
        (_b = (_a = this.props).onScaleChange) === null || _b === void 0 ? void 0 : _b.call(_a, scale, this.adaptScale, this.minScale);
    };
    LoadableImage.prototype.moveTo = function (x, y) {
        this.setState({
            translateX: x,
            translateY: y
        });
    };
    LoadableImage.prototype.processDeltaYForFirefox = function (deltaY) {
        var targetDelta = deltaY * FIREFOX_ZOOM_SENSITIVITY;
        var targeRange = deltaY > 0 ? [40, 50] : [-50, -40];
        if (deltaY !== 0) {
            return castInto(targetDelta, targeRange);
        }
        return 0;
    };
    LoadableImage.prototype.scaleByWheel = function (deltaY) {
        var minScale = this.minScale;
        var maxScale = this.props.maxScale;
        var scale = this.state.scale;
        scale = scale + -1 * deltaY / 1000;
        if (scale > maxScale) {
            scale = maxScale;
        }
        else if (scale < minScale) {
            scale = minScale;
        }
        else if (scale >= 0.96 && scale < 1) {
            scale = 1;
        }
        else if (scale <= 1.04 && scale > 1) {
            scale = 1;
        }
        this.scaleTo(scale);
    };
    LoadableImage.prototype.adaptImagePosition = function () {
        var _a = this.getDistances(), leftDistance = _a.leftDistance, rightDistance = _a.rightDistance, topDistance = _a.topDistance, bottomDistance = _a.bottomDistance;
        if ((bottomDistance > 0 && topDistance < 0) ||
            (bottomDistance < 0 && topDistance > 0) ||
            (rightDistance > 0 && leftDistance < 0) ||
            (rightDistance < 0 && leftDistance > 0)) {
            this.setState({ translateX: 0, translateY: 0 });
        }
    };
    LoadableImage.prototype.getDistances = function () {
        var imageWrapper = this.imageWrapperRef.current;
        if (!imageWrapper || !this.rootContainer) {
            return {
                leftDistance: 0, rightDistance: 0, topDistance: 0, bottomDistance: 0
            };
        }
        var _a = imageWrapper.getBoundingClientRect(), leftDistance = _a.left, topDistance = _a.top, right = _a.right, bottom = _a.bottom;
        var _b = this.rootContainer.getBoundingClientRect(), rootBottom = _b.bottom, rootRight = _b.right;
        var bottomDistance = rootBottom - bottom;
        var rightDistance = rootRight - right;
        return {
            leftDistance: leftDistance,
            rightDistance: rightDistance,
            topDistance: topDistance,
            bottomDistance: bottomDistance,
        };
    };
    LoadableImage.prototype.getClippedResult = function () {
        return calcClippedResult(this.imageRef.current, this.props.clipStyle);
    };
    LoadableImage.prototype.calcCurrentImageAdaptScale = function () {
        var _a = this.getClippedResult(), width = _a.clippedWidth, height = _a.clippedHeight;
        var adaptScale = 1;
        if (!this.rootContainer) {
            return adaptScale;
        }
        var _b = this.rootContainer.getBoundingClientRect(), rootWidth = _b.width, rootHeight = _b.height;
        var safeAreaInset = this.props.safeAreaInset;
        var safeImageWidth = rootWidth - safeAreaInset * 2;
        var safeImageHeight = rootHeight - safeAreaInset * 2;
        if (safeImageWidth <= 0 || safeImageHeight <= 0) {
            return adaptScale;
        }
        if (width > safeImageWidth || height > safeImageHeight) {
            adaptScale = Math.min(safeImageWidth / width, safeImageHeight / height);
        }
        return adaptScale;
    };
    LoadableImage.prototype.getImageWrapperStyle = function () {
        var _a = this.props, rotateDegree = _a.rotateDegree, clipStyle = _a.clipStyle;
        var _b = this.state, translateX = _b.translateX, translateY = _b.translateY, scale = _b.scale;
        var imageWrapperStyle = {};
        if (clipStyle) {
            var _c = this.getClippedResult(), clippedWidth = _c.clippedWidth, clippedHeight = _c.clippedHeight;
            imageWrapperStyle.width = clippedWidth;
            imageWrapperStyle.height = clippedHeight;
        }
        imageWrapperStyle.transform = "scale(" + scale + ") translate(" + translateX + "px, " + translateY + "px) rotate(" + rotateDegree + "deg)";
        return imageWrapperStyle;
    };
    LoadableImage.prototype.getImageStyle = function () {
        var _a, _b;
        var image = this.imageRef.current;
        var clipStyle = this.props.clipStyle;
        var result = {};
        if (image === null || image === void 0 ? void 0 : image.complete) {
            result.width = (_a = clipStyle === null || clipStyle === void 0 ? void 0 : clipStyle.originWidth) !== null && _a !== void 0 ? _a : image.naturalWidth;
            result.height = (_b = clipStyle === null || clipStyle === void 0 ? void 0 : clipStyle.originHeight) !== null && _b !== void 0 ? _b : image.naturalHeight;
        }
        if (!clipStyle) {
            return result;
        }
        var _c = this.getClippedResult(), clippedLeft = _c.clippedLeft, clippedTop = _c.clippedTop;
        result.marginLeft = -1 * clippedLeft;
        result.marginTop = -1 * clippedTop;
        return result;
    };
    LoadableImage.prototype.render = function () {
        return consumeTheme(this.themedRender);
    };
    return LoadableImage;
}(React.Component));
export default LoadableImage;
LoadableImage.defaultProps = {
    prefixCls: 'dui-preview-image',
    onContextMenu: emptyFn,
    rotateDegree: 0,
};
